// Single in-flight CSRF refresh shared by all requests - parallel 419s (e.g. image uploads
// via Promise.all) would otherwise each start a new session and overwrite each other's token
let csrfRefreshPromise: Promise<void> | null = null
const refreshCsrfCookie = () => {
	// module state is shared between users on the server - dedupe only in the browser
	if (process.server) return ApiService.setCookies()
	csrfRefreshPromise ??= ApiService.setCookies().finally(() => {
		csrfRefreshPromise = null
	})
	return csrfRefreshPromise
}

export const apiCommon = async <T>(request: any, options: any, lang = '*', currency?: string): Promise<IApiCommon<T>> => {
	const config = useNuxtApp().$config
	const setParameters = () => {
		const currencyCookie = process.client ? useCookie('currency').value : null
		const currencyProp = currency

		const currencyForHeader = currencyProp || currencyCookie || 'PLN'
		// const xsrfToken = useCookie('XSRF-TOKEN')
		let headers = {
			accept: 'application/json',
			'Accept-Language': lang,
			Currency: currencyForHeader,
			...options?.headers,
		}

		// if (config.public.previewMode) {
		// 	const token = localStorage?.getItem('token')
		// 	headers.Authorization = `Bearer ${token}`
		// }

		if (process.client) {
			const xsrfToken = useCookie('XSRF-TOKEN')
			if (xsrfToken && xsrfToken.value) {
				headers['X-XSRF-TOKEN'] = xsrfToken.value
			}
		}

		if (process.server) {
			headers = {
				...headers,
				...useRequestHeaders(['cookie']),
				'X-Internal-Api-Key': config.internalApiKey,
				// referer: config.public.baseURL,
			}
		}
		const obj = {
			baseURL: config.public.apiBaseURL,
			headers,
			credentials: 'include',
			mode: 'cors',
			...options,
		}
		return obj
	}

	const isCookieRequest = () => {
		return request === 'sanctum/csrf-cookie'
	}

	// Jeśli sanctum/csrf-cookie to zmień url
	const url = isCookieRequest() ? config.public.apiBaseURL : `${config.public.apiBaseURL}/api`

	const fetchData = async () => {
		try {
			if ((import.meta.server === true || options?.server === true) && !options?.skipSSRPayload) {
				const response = await useFetch<IApiCommon<T>>(`${url}/${request}`, { ...setParameters(), server: true })
				if (response.error.value) throw response.error.value
				if (response.error) return { ...response, ...response.data.value }
			} else {
				const response = await $fetch<IApiCommon<T>>(`${url}/${request}`, setParameters())
				return response && response.meta ? { ...response, meta: ApiMeta(response.meta) } : response
			}
		} catch (error: any) {
			if (!options.signal?.aborted) {
				logger.log(error)

				const receivedData = { ...error.data, status: error?.response?.status ?? error?.statusCode }
				if (receivedData.status === 403 && receivedData.message === 'Your account has been suspended') {
					useUserStore().logout()
				}
				throw receivedData
			}
		}
	}

	try {
		if (!isCookieRequest() && process.client) {
			const xsrfToken = useCookie('XSRF-TOKEN')
			if (!xsrfToken || !xsrfToken.value) {
				await refreshCsrfCookie()
			}
		}

		return await fetchData()
	} catch (error: any) {
		if (error.status === 419 && !isCookieRequest()) {
			await refreshCsrfCookie()
			return await fetchData()
		}
		throw error
	}
}
